import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MakeQuestionAttempted, GetMocktestQuestion } from "../../../Client/request";
import { createMockTestPayload } from "../utils/apiPayload";
import { getScore, calculateScoreAndIndexes } from "../utils/readingScoring";
import { hasValidUserResponse } from "./useReadingValidation";

export const useReadingSubmission = ({
  quest,
  questData,
  selectedOptions,
  SelectedAnswers,
  textAnswers,
  userOrder,
  questionsTime,
  MockTestAttemptID,
  ExamId,
  step,
  totalQuestions,
  commulative,
  setStep,
  setSelectedOptions,
  setSelectedAnswers,
  setTextAnswers,
  setUserOrder,
  setCommulative,
  stopStopwatch,
  resetQuestionTimer,
  submitTriggerRef,
  questions,
  endTestAndTimeout,
  navigateOnErrorPath = "/Mocktest"
}) => {
  const [networkError, setNetworkError] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (submitTriggerRef && submitTriggerRef.current) {
      if (hasValidUserResponse(quest, questData, selectedOptions, SelectedAnswers, textAnswers, userOrder)) {
        handleNext(); 
      }
      
      forceSubmitLastReadingQuestion();
      submitTriggerRef.current = false;
    }
  }, [submitTriggerRef?.current]);

  const handleNext = () => {
    if (!hasValidUserResponse(quest, questData, selectedOptions, SelectedAnswers, textAnswers, userOrder)) { return; }
    if (step === totalQuestions) {
      setDisabled(true);
    }
    stopStopwatch();
    setCommulative(commulative + questionsTime / 1000);

    if (quest?.SubCategory === "Reading & Writing: Fill in the Blanks") {
      const score = getScore(questData?.AnswerNames, textAnswers);
      const finalPayload = createMockTestPayload({
        quest,
        questData,
        userResponse: textAnswers,
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        selectedAnswers: textAnswers,
        correctAnswers: questData?.AnswerNames
      });

      MakeQuestionAttempted(finalPayload).then((res) => {
        setSelectedAnswers([]);
        setTextAnswers([""]);
        handleResponse(res);
      });
    } else if (quest?.SubCategory === "Multiple Choice, Multiple Answers") {
      let score = getScore(questData?.AnswerNames, selectedOptions, 2);
      const finalPayload = createMockTestPayload({
        quest,
        questData,
        userResponse: selectedOptions,
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        selectedAnswers: selectedOptions,
        correctAnswers: questData?.AnswerNames
      });

      MakeQuestionAttempted(finalPayload).then((res) => {
        setSelectedOptions([]);
        handleResponse(res);
      });
    } else if (quest?.SubCategory === "Re-order Paragraphs") {
        const finalUserOrder = userOrder?.length > 0 ? userOrder : questData?.OptionNames?.map((_, index) => index) || [];
      let result = calculateScoreAndIndexes(questData?.AnswerNames, questData?.OptionNames, finalUserOrder);
      
      const finalPayload = createMockTestPayload({
        quest,
        questData,
        userResponse: finalUserOrder,
        marksObtained: result.score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        selectedAnswers: finalUserOrder,
        correctAnswers: result.correctIndexes,
        userOrder: result.userIndexes
      });

      MakeQuestionAttempted(finalPayload).then((res) => {
        setUserOrder([]);
        handleResponse(res);
      });
    } else if (quest?.SubCategory === "Reading: Fill in the Blanks") {
      let score = getScore(questData?.AnswerNames, SelectedAnswers);
      const finalPayload = createMockTestPayload({
        quest,
        questData,
        userResponse: SelectedAnswers,
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        selectedAnswers: SelectedAnswers,
        correctAnswers: questData?.AnswerNames
      });

      MakeQuestionAttempted(finalPayload).then((res) => {
        setSelectedAnswers([]);
        handleResponse(res);
      });
    } else if (quest?.SubCategory === "Multiple Choice, Single Answer") {
      let score = getScore(questData?.AnswerNames, [selectedOptions]);
      const finalPayload = createMockTestPayload({
        quest,
        questData,
        userResponse: [selectedOptions],
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        selectedAnswers: [selectedOptions],
        correctAnswers: questData?.AnswerNames
      });

      MakeQuestionAttempted(finalPayload).then((res) => {
        setSelectedOptions([]);
        handleResponse(res);
      });
    }

    resetQuestionTimer();
    if (step !== totalQuestions) {
      setStep(step + 1);
    }
  };

  const handleResponse = (res) => {
    if (res?.error) {
      setNetworkError(true);
      setTimeout(() => {
        navigate(navigateOnErrorPath);
      }, 2000);
    } else {
      if (!quest?.IsAttempted) {
        quest.IsAttempted = true;
      }
    }
    // If this was the final question overall, ask parent to end the test
    if (!res?.error && step === totalQuestions && typeof endTestAndTimeout === 'function') {
      endTestAndTimeout().then(() => {});
    }
  };


  const forceSubmitLastReadingQuestion = () => {
    const readingQuestions = questions?.filter((q) => q?.Category === "Reading") || [];
    if (!readingQuestions?.length) return;

    const lastReadingQ = readingQuestions[readingQuestions?.length - 1];

    if (lastReadingQ?.QuestionId === quest?.QuestionId && !hasValidUserResponse(quest, questData, selectedOptions, SelectedAnswers, textAnswers, userOrder)) {
      return;
    }

    GetMocktestQuestion(lastReadingQ?.QuestionId).then((res) => {
      if (res?.data) {
        const fakeScore = -100;
        const formData = new FormData();
        
        formData.append("mock_test_question_id", Number(lastReadingQ?.MockTestQuestionTableId));
        formData.append("mock_test_attempt_id", Number(MockTestAttemptID || ExamId));
        formData.append("marks_obtained", Number(fakeScore));
        formData.append("is_ptecore", false);
        formData.append("time_taken", Number(0));
        
        formData.append("all_times.Category", lastReadingQ?.Category);
        formData.append("all_times.commulativeTime", Number(commulative));
        
        const forcedUserResponse = {
          QuestionId: lastReadingQ.QuestionId,
          // QuestionStatement: res.data.QuestionStatement,
          QuestionStatement: "This is a fake question statement. Auto-submitted on time-up",
          QuestionName: lastReadingQ.QuestionName,
          Category: lastReadingQ.Category,
          SubCategory: lastReadingQ.SubCategory,
          autoAttemptedLastOnTimeUp: true
        };

        if (lastReadingQ.SubCategory.includes('Fill in the Blanks')) {
          forcedUserResponse.correctAnswers = ["fake_answer_1", "fake_answer_2"];
          forcedUserResponse.selectedAnswers = ["fake_selection_1", "fake_selection_2"];
        } else if (lastReadingQ.SubCategory.includes('Multiple Choice')) {
          forcedUserResponse.correctAnswers = ["fake_correct_answer"];
          forcedUserResponse.selectedAnswers = ["fake_selected_answer"];
        } else if (lastReadingQ.SubCategory.includes('Re-order Paragraphs')) {
          forcedUserResponse.submissionResult = {
            userIndexes: [0, 1, 2],
            correctIndexes: [0, 1, 2],
            score: 0
          };
        }
        
        Object.entries(forcedUserResponse).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            // Handle arrays with indexed keys
            value.forEach((item, index) => {
              formData.append(`user_response.${key}[${index}]`, item);
            });
          } else if (typeof value === 'object' && value !== null) {
            // Handle nested objects
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
              formData.append(`user_response.${key}.${nestedKey}`, nestedValue);
            });
          } else {
            // Handle simple values
            formData.append(`user_response.${key}`, value);
          }
        });
        
        let component = "Choice";
        if (lastReadingQ.SubCategory.includes('Fill in the Blanks')) {
          component = "Blanks";
        } else if (lastReadingQ.SubCategory.includes('Multiple Choice')) {
          component = "Choice";
        }
        
        formData.append("user_response.enableSkillsData[0].component", component);
        formData.append("user_response.enableSkillsData[0].score", "-100/1");
        formData.append("user_response.enableSkillsData[0].suggestion", "Auto-submitted on time-up");
        
        formData.append("attempt_info.test_name", lastReadingQ.SubCategory);
        formData.append("attempt_info.attempt_mode", "mock");
        
        const forcedPayload = formData;

        MakeQuestionAttempted(forcedPayload)
          .then((response) => {
            console.log("Successfully forced submission of last Reading question.", response);
            lastReadingQ.IsAttempted = true;
          })
          .catch((err) => {
            console.error("Error forcibly submitting last Reading question: ", err);
          });
      }
    });
  };

  return {
    networkError,
    setNetworkError,
    disabled,
    setDisabled,
    handleNext
  };
};
